/**
 * STAIRS (spec §60I).
 *
 * A staircase in a renovation estimate is a handful of numbers and one or
 * two choices — not a piece of structural engineering. Berrier Reno records
 * what the work is priced from and what the drawing needs to show; it does
 * not check code compliance, does not calculate loads, and does not certify
 * anything. Where a figure is unknown it stays unknown.
 *
 * Measurements follow the Phase 3 convention: exact integers ×100 (so 7.75"
 * is 775), never binary floating point.
 */

export type StairConfiguration = "straight" | "l-turn" | "u-turn" | "winder" | "custom";

export const STAIR_CONFIGURATIONS: ReadonlyArray<{ id: StairConfiguration; label: string; hint: string }> = [
  { id: "straight", label: "Straight", hint: "One flight, no turn." },
  { id: "l-turn", label: "L / quarter-turn", hint: "One landing, a quarter turn." },
  { id: "u-turn", label: "U / half-turn", hint: "One landing, doubles back." },
  { id: "winder", label: "Winder", hint: "Turns on wedge-shaped treads." },
  { id: "custom", label: "Custom", hint: "Anything else — price it as a quote." },
];

/** Which way UP runs along the drawn footprint. */
export type StairUpDirection = "forward" | "back";

export interface Stairs {
  objectId: string;
  configuration: StairConfiguration;
  upDirection: StairUpDirection;
  /** Floor to floor, inches ×100. Null = not measured. */
  totalRiseX100: number | null;
  riserCount: number | null;
  treadCount: number | null;
  treadDepthX100: number | null;
  riserHeightX100: number | null;
  hasLanding: boolean;
  handrail: boolean;
  guardrail: boolean;
  finishNotes: string;
  notes: string;
}

export interface StairsInput {
  configuration: StairConfiguration;
  upDirection: StairUpDirection;
  totalRiseX100: number | null;
  riserCount: number | null;
  treadCount: number | null;
  treadDepthX100: number | null;
  riserHeightX100: number | null;
  hasLanding: boolean;
  handrail: boolean;
  guardrail: boolean;
  finishNotes: string;
  notes: string;
}

export function emptyStairsInput(): StairsInput {
  return {
    configuration: "straight",
    upDirection: "forward",
    totalRiseX100: null,
    riserCount: null,
    treadCount: null,
    treadDepthX100: null,
    riserHeightX100: null,
    hasLanding: false,
    handrail: true,
    guardrail: false,
    finishNotes: "",
    notes: "",
  };
}

export function validateStairsInput(input: StairsInput): string[] {
  const problems: string[] = [];
  const positive = (value: number | null, label: string) => {
    if (value !== null && value <= 0) problems.push(`${label} must be more than zero.`);
    if (value !== null && !Number.isInteger(value)) problems.push(`${label} must be a whole number of hundredths.`);
  };
  positive(input.totalRiseX100, "Total rise");
  positive(input.treadDepthX100, "Tread depth");
  positive(input.riserHeightX100, "Riser height");
  if (input.riserCount !== null && (!Number.isInteger(input.riserCount) || input.riserCount <= 0)) {
    problems.push("Riser count must be a whole number more than zero.");
  }
  if (input.treadCount !== null && (!Number.isInteger(input.treadCount) || input.treadCount <= 0)) {
    problems.push("Tread count must be a whole number more than zero.");
  }
  return problems;
}

/**
 * The two numbers a person usually knows are the floor-to-floor height and
 * how many risers there are; the rest follows. Nothing here is a code check
 * — it fills in what can be derived and leaves the rest alone.
 */
export function deriveStairFigures(input: StairsInput): StairsInput {
  const derived = { ...input };
  if (derived.riserHeightX100 === null && derived.totalRiseX100 !== null && derived.riserCount) {
    derived.riserHeightX100 = Math.round(derived.totalRiseX100 / derived.riserCount);
  }
  if (derived.riserCount === null && derived.totalRiseX100 !== null && derived.riserHeightX100) {
    derived.riserCount = Math.max(1, Math.round(derived.totalRiseX100 / derived.riserHeightX100));
  }
  // A flight has one fewer tread than risers when it lands on the floor above.
  if (derived.treadCount === null && derived.riserCount !== null) {
    derived.treadCount = Math.max(1, derived.riserCount - 1);
  }
  return derived;
}

/**
 * How many STEPS the per-step assemblies (treads and risers, refinishing)
 * should be priced for. Null when nobody has said yet — an estimate that
 * quietly assumes 13 steps is worse than one that says it does not know.
 */
export function stairStepCount(stairs: Pick<Stairs, "treadCount" | "riserCount">): number | null {
  if (stairs.treadCount !== null && stairs.treadCount > 0) return stairs.treadCount;
  if (stairs.riserCount !== null && stairs.riserCount > 0) return Math.max(1, stairs.riserCount - 1);
  return null;
}

/** A one-line description for the inspector and the printed sheet. */
export function describeStairs(stairs: Stairs): string {
  const label = STAIR_CONFIGURATIONS.find((c) => c.id === stairs.configuration)?.label ?? stairs.configuration;
  const steps = stairStepCount(stairs);
  const parts = [label];
  if (steps !== null) parts.push(`${steps} steps`);
  if (stairs.hasLanding) parts.push("landing");
  if (stairs.handrail) parts.push("handrail");
  if (stairs.guardrail) parts.push("guardrail");
  return parts.join(" · ");
}
